// 「记录交易」弹窗的候选账本（spec: docs/sdd/astock-agent-transaction-dialog-ledger-edit/spec.md）。
//
// 提交是完整账本：历史行带 transaction_id 回传，不带 id 的是新增，没回传的旧 id 就是删除。
// 所以这里有两条硬规矩：
//   1. 历史行一律 {...stored, ...edits}——stored 是写入目标的原始事实，一个字段都不许丢；
//   2. 录入持仓可以有多批（2026-09-16）：每批是「某天从别的账户转入 N 股」，已提交的
//      每批在历史区逐条改、删（openings）；「录入持仓」tab 只管新增一批（newOpening），
//      和「录入交易记录」tab 一致。原来一个持仓只有一份录入持仓，tab 和历史行改的是
//      同一份，用户在 tab 里一改，历史区就跟着变，看起来像没提交就进了历史。
// 预填必须来自写入目标本身（spec §3.3），这个模块不管数据从哪来，调用方负责。

const EDIT_FIELDS = ["trade_date", "action", "quantity", "price", "actual_fee"];
const OPENING_FIELDS = ["quantity", "cost_price", "total_cost", "effective_date"];
const CONFIRMATION_FIELDS = ["price_confirmation_key", "_price_confirmation"];

let keySeed = 0;
const newKey = () => `new-${Date.now().toString(36)}-${(keySeed += 1)}`;

const text = (value) => (value === null || value === undefined ? "" : String(value).trim());

function sameValue(field, left, right) {
  if (field === "trade_date" || field === "action") return text(left) === text(right);
  const a = text(left);
  const b = text(right);
  if (!a || !b) return a === b;
  const numericA = Number(a);
  const numericB = Number(b);
  return Number.isFinite(numericA) && Number.isFinite(numericB) ? numericA === numericB : a === b;
}

const num = (value) => {
  const raw = text(value);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

// 「数量 × 成本价」和总成本对得上＝其中一项是算出来的（成本价两位小数，留半分余量）
function costsAgree(opening) {
  const [quantity, price, total] = [num(opening?.quantity), num(opening?.cost_price), num(opening?.total_cost)];
  if (quantity === null || price === null || total === null) return true;
  return Math.abs(quantity * price - total) <= quantity * 0.005 + 0.01;
}

// 录入持仓回填进表单的值（AGENT-125）。只回填用户当初给的那一项成本；另一项是服务端算的，
// 回填进输入框再提交就会被当成用户输入原样存下——改了数量或成本价，总成本却停在旧值。
// 两项对不上说明两个都是用户自己填的（总成本含手续费，或者填错了），这时两个都回填。
export function openingFormValues(opening) {
  if (!opening) return null;
  const given = text(opening.cost_source) === "total_cost" ? "total_cost" : "cost_price";
  const other = given === "total_cost" ? "cost_price" : "total_cost";
  return {
    quantity: opening.quantity ?? "",
    effective_date: opening.effective_date ?? "",
    [given]: opening[given] ?? "",
    [other]: costsAgree(opening) ? "" : (opening[other] ?? ""),
  };
}

// 留空的那一项成本，服务端会算成多少——给输入框当占位文字，用户看得见「没填就用计算值」
export function derivedOpeningCosts(opening) {
  const [quantity, price, total] = [num(opening?.quantity), num(opening?.cost_price), num(opening?.total_cost)];
  if (!quantity) return { cost_price: null, total_cost: null };
  return {
    cost_price: price === null && total !== null ? String(Number((total / quantity).toFixed(4))) : null,
    total_cost: total === null && price !== null ? (quantity * price).toFixed(2) : null,
  };
}

// 两项都填了但差出 10% 以上时提示、不拦：手续费通常在 1% 以内，差出几倍多半是多打了位数
// （线上泸州老窖 1000 股 × 50 元，总成本填成了 5165156）。
export function openingCostGap(opening) {
  const [quantity, price, total] = [num(opening?.quantity), num(opening?.cost_price), num(opening?.total_cost)];
  if (!quantity || !price || total === null) return null;
  const expected = quantity * price;
  return Math.abs(total - expected) > expected * 0.1 ? { expected: expected.toFixed(2) } : null;
}

function pick(source, fields) {
  return Object.fromEntries(
    fields.filter((field) => source?.[field] !== undefined).map((field) => [field, source[field]]),
  );
}

function withoutConfirmation(row) {
  return Object.fromEntries(
    Object.entries(row).filter(([field]) => !CONFIRMATION_FIELDS.includes(field)),
  );
}

// 老数据的第一批没有 batch_id，用这个虚拟 id 回传（后端 OPENING_LEGACY_BATCH_ID）
const LEGACY_BATCH_ID = "opening";
const BLANK_OPENING = { quantity: "", cost_price: "", total_cost: "", effective_date: "" };

// opening_position 这一份 JSON 里装着全部批次：第一批是它本身，其余在 additional_batches
export function openingBatchesOf(openingPosition) {
  if (!openingPosition) return [];
  const { additional_batches: rest = [], ...first } = openingPosition;
  return [first, ...rest];
}

export function seedLedgerDraft({ openingPosition = null, transactions = [], proposal = null }) {
  const history = transactions.map((row, index) => ({
    key: text(row.transaction_id) || `history-${index}`,
    stored: row,
    edits: {},
    deleted: false,
  }));
  let draft = {
    tab: "transactions",
    storedOpening: openingPosition,
    openings: openingBatchesOf(openingPosition).map((batch) => ({
      key: text(batch.batch_id) || LEGACY_BATCH_ID,
      stored: batch,
      values: openingFormValues(batch),
      deleted: false,
    })),
    newOpening: { ...BLANK_OPENING },
    history,
    newRows: [],
  };
  if (proposal?.kind === "opening_position") {
    // Agent 提议里同样只带用户给的那一项成本，算出来的另一项清空、交给服务端重算。
    // 只有一批时，Agent 的「更正当前持仓」改的就是这一批；否则填进新增表单，由用户决定。
    const facts = proposal.facts ?? {};
    const values = pick(openingFormValues(facts), OPENING_FIELDS.filter((field) => facts[field] !== undefined));
    draft = draft.openings.length === 1
      ? { ...setOpeningBatch(draft, draft.openings[0].key, values), tab: "holding" }
      : { ...setNewOpening(draft, values), tab: "holding" };
  }
  if (proposal?.kind === "transactions") {
    const updates = new Map((proposal.updates ?? []).map((row) => [text(row.transaction_id), row]));
    const deletions = new Set((proposal.deletions ?? []).map(text));
    draft.history = draft.history.map((row) => ({
      ...row,
      // Agent 提案里的确认凭据一律不收：确认只能由用户在弹窗里点
      edits: updates.has(row.key) ? pick(updates.get(row.key), EDIT_FIELDS) : row.edits,
      deleted: deletions.has(row.key),
    }));
    draft.newRows = (proposal.additions ?? []).map((row) => ({
      key: newKey(),
      ...pick(row, EDIT_FIELDS),
    }));
  }
  return draft;
}

export function setNewOpening(draft, patch) {
  return { ...draft, newOpening: { ...draft.newOpening, ...patch } };
}

export function setOpeningBatch(draft, key, patch) {
  return {
    ...draft,
    openings: draft.openings.map((batch) => (
      batch.key === key ? { ...batch, values: { ...batch.values, ...patch } } : batch
    )),
  };
}

export function undoOpeningBatch(draft, key) {
  return {
    ...draft,
    openings: draft.openings.map((batch) => (
      batch.key === key ? { ...batch, values: openingFormValues(batch.stored), deleted: false } : batch
    )),
  };
}

export function setOpeningBatchDeleted(draft, key, deleted) {
  return {
    ...draft,
    openings: draft.openings.map((batch) => (batch.key === key ? { ...batch, deleted } : batch)),
  };
}

export function editHistoryRow(draft, key, patch) {
  return {
    ...draft,
    history: draft.history.map((row) => (
      row.key === key ? { ...row, edits: { ...row.edits, ...patch } } : row
    )),
  };
}

export function setHistoryDeleted(draft, key, deleted) {
  return {
    ...draft,
    history: draft.history.map((row) => (row.key === key ? { ...row, deleted } : row)),
  };
}

export function addNewRow(draft, row = {}) {
  return { ...draft, newRows: [...draft.newRows, { key: newKey(), ...row }] };
}

export function patchNewRow(draft, index, patch) {
  return {
    ...draft,
    newRows: draft.newRows.map((row, current) => (current === index ? { ...row, ...patch } : row)),
  };
}

export function removeNewRow(draft, index) {
  return { ...draft, newRows: draft.newRows.filter((_, current) => current !== index) };
}

export function historyRowState(row) {
  if (row.deleted) return "deleted";
  const edited = Object.entries(row.edits).some(
    ([field, value]) => !sameValue(field, value, row.stored[field]),
  );
  return edited ? "edited" : "unchanged";
}

export function openingBatchState(batch) {
  if (batch.deleted) return "deleted";
  const seeded = openingFormValues(batch.stored);
  const edited = OPENING_FIELDS.some(
    (field) => !sameValue(field === "effective_date" ? "trade_date" : field,
      batch.values?.[field], seeded[field]),
  );
  return edited ? "edited" : "unchanged";
}

export const isBlankOpening = (opening) => !OPENING_FIELDS.some((field) => text(opening?.[field]));

const isBlankNewRow = (row) => !text(row.quantity) && !text(row.price);

function submittedRows(draft) {
  return [
    ...draft.history.filter((row) => !row.deleted),
    ...draft.newRows.filter((row) => !isBlankNewRow(row)),
  ];
}

function openingPayload(values, batchId, unit) {
  return {
    batch_id: batchId,
    quantity: text(values.quantity),
    cost_price: text(values.cost_price) || null,
    total_cost: text(values.total_cost) || null,
    quantity_unit: unit,
    effective_date: text(values.effective_date) || null,
  };
}

export function buildLedgerPayload(draft, { assetCode, unit = "股" }) {
  return {
    opening_positions: [
      ...draft.openings
        .filter((batch) => !batch.deleted)
        .map((batch) => openingPayload(batch.values, batch.key, unit)),
      ...(isBlankOpening(draft.newOpening) ? [] : [openingPayload(draft.newOpening, null, unit)]),
    ],
    transactions: submittedRows(draft).map((row) => {
      if (row.stored) {
        // 用户在 409 之后点「确认价格」时，调用方把凭据写进 edits.price_confirmation_key
        return {
          ...row.stored,
          ...withoutConfirmation(row.edits),
          ...pick(row.edits, ["price_confirmation_key"]),
        };
      }
      return {
        asset_code: assetCode,
        ...pick(row, EDIT_FIELDS),
        ...(row.price_confirmation_key ? { price_confirmation_key: row.price_confirmation_key } : {}),
      };
    }),
  };
}

// 什么都没改就不该提交：提交一次就多一个 revision 和一组审计，却没有任何事实变化。
export function hasChanges(draft) {
  return draft.history.some((row) => historyRowState(row) !== "unchanged")
    || draft.openings.some((batch) => openingBatchState(batch) !== "unchanged")
    || !isBlankOpening(draft.newOpening)
    || draft.newRows.some((row) => !isBlankNewRow(row));
}

export function isDeleteAll(draft) {
  const hadFacts = Boolean(draft.storedOpening) || draft.history.length > 0;
  const payload = buildLedgerPayload(draft, { assetCode: "" });
  return hadFacts && payload.opening_positions.length === 0 && payload.transactions.length === 0;
}

// 问题落在哪个输入框：只标相关的那个，不再整行都标（2026-09-15 走查 W-02）。
// 不认识的问题只高亮整行、显示原因，不猜是哪个框。
const ISSUE_FIELDS = [
  [/^dq_(trade_price_out_of_range|fund_nav_mismatch)/, ["price"]],
  [/^dq_negative_position_/, ["quantity"]],
  [/^dq_invalid_trade_value/, ["quantity", "price"]],
  [/^dq_(invalid_trade_date|trade_date_in_future|trade_after_cutoff|trade_day_missing_price|corporate_action_same_day_trade|rights_issue_same_day_trade)/, ["trade_date"]],
  [/^dq_unsupported_trade_action/, ["action"]],
];

export function issueFields(issue) {
  const id = String(issue?.issue_id || issue?.code || "");
  return ISSUE_FIELDS.find(([pattern]) => pattern.test(id))?.[1] ?? [];
}

// 被挡住的问题按行归并：{ text: 该行全部原因, fields: 需要标出的输入框 }；归不到行的原样留在 unmapped。
export function groupIssuesByRow(draft, issues) {
  const byKey = new Map();
  const unmapped = [];
  (issues || []).forEach((issue) => {
    const key = issueRowKey(draft, issue);
    const message = issue?.message || issue?.issue_id;
    if (!key) {
      if (message) unmapped.push(message);
      return;
    }
    const current = byKey.get(key) ?? { text: "", fields: new Set() };
    byKey.set(key, {
      text: [current.text, message].filter(Boolean).join("\n"),
      fields: new Set([...current.fields, ...issueFields(issue)]),
    });
  });
  return { byKey, unmapped };
}

export function issueRowKey(draft, issue) {
  const number = issue?.row_number;
  if (!Number.isInteger(number) || number < 1) return null;
  return submittedRows(draft)[number - 1]?.key ?? null;
}
